import { getPlatform } from '../src/utils/platform'
import { useLogger } from './useLogger'
import { APP_STORE_URL, PLAY_STORE_URL } from '../src/constants'
import { Platform } from '../src/types'
import { useAuthStore } from '../stores/authStore'
import { useStartWizardStore } from '../stores/startWizardStore'
import { useSupabaseClientStore } from '../stores/supaBaseClientStore'

// TypeScript declaration for Vite env
declare global {
  interface ImportMetaEnv {
    VITE_NUXT_PUBLIC_SITE_URL: string
  }
}

const SITE_URL = import.meta.env.VITE_NUXT_PUBLIC_SITE_URL

const APP_SCHEME = 'com.usenestor.app'
const APP_HOST = 'app.usenestor.com'

export function useAuthRedirects() {
  const logger = useLogger()

  /** 
  Get the base WEB URL for the app - depends on the environment.
  It is where the server is IF we are talking to a web server.
  Either set explicitly in VITE_NUXT_PUBLIC_SITE_URL as it is on Netlify (for either ddebugg.netlify.app or app.usenestor.com)
  If not set explicitly, set to APP_HOST (app.usenestor.com) when in compiled to production (this is the default when compiling the apk), or localhost:5363 when in development
  **/
  const getBaseUrl = () => {
    logger.debug("Auth Redirects: Getting base URL", {
      rawSiteUrl: SITE_URL || 'import.meta.env.VITE_NUXT_PUBLIC_SITE_URL not set',
      nodeEnv: process.env.NODE_ENV,
      isProd: process.env.NODE_ENV === 'production'
    })

    const siteUrl = SITE_URL || (process.env.NODE_ENV === 'production'
      ? `https://${APP_HOST}`  // Production fallback
      : 'http://localhost:5363')     // Development fallback

    logger.debug("Auth Redirects: Resolved base URL", {
      siteUrl,
      usedFallback: !SITE_URL,
      fallbackType: process.env.NODE_ENV === 'production' ? 'production' : 'development'
    })

    return siteUrl
  }

  /**
   * Generate a platform-specific URL for the given path in the app
   * @param path The path part of the URL (without leading slash)
   * @param debugContext Context for logging
   * @param queryParams Query parameters to add to the URL
   * @param forceWeb Force the URL to be web (for universal invite link landing on web)
   * @returns Platform-specific URL
   */
  const getPlatformUrl = (path: string, debugContext: string, queryParams: Record<string, string> | null = null, forceWeb: boolean = false) => {
    const platform: Platform = forceWeb ? Platform.Web : getPlatform()

    logger.debug(`Auth Redirects: Getting ${debugContext}`, { platform })

    const baseUrl = getBaseUrl()
    let url = platform === Platform.Web
      ? `${baseUrl}/${path}`
      : `${APP_SCHEME}://${APP_HOST}/${path}`

    if (queryParams) {
      url += `?${new URLSearchParams(queryParams).toString()}`
    }

    logger.debug(`Auth Redirects: Generated ${debugContext}`, {
      platform,
      baseUrl,
      url,
      isWebPlatform: platform === Platform.Web
    })

    return url
  }

  /*
  Get the store URL based on the platform.
  If the platform is not supported return the app's base URL on web
  */
  const getStoreUrl = (queryParams: Record<string, string> | null = null) => {
    const platform: Platform = getPlatform()
    switch (platform) {
      case Platform.iOS:
        // iOS Universal Links will handle the parameters when app is installed
        return APP_STORE_URL
      case Platform.Android: {
        // Create the HTTPS URL for App Links to work after install
        const deepLinkUrl = `https://${APP_HOST}/auth/callback-invite${queryParams ? `?${new URLSearchParams(queryParams).toString()}` : ''}`
        // Android allows referrer parameter for Play Store
        return `${PLAY_STORE_URL}&referrer=${encodeURIComponent(deepLinkUrl)}`
      }
      default:
        logger.error("Auth Redirects: platform expected to be ios or android, but not found, returning web main url", { redirectTo: getBaseUrl() })
        return getBaseUrl()
    }
  }

  /** 
  Get the home URL on any platform - points to home on the platform that the user is on right now
  using a direct link to / on we and a link to a deep link, landonhome on mobile
  **/
  const getHomeUrl = () => {
    const platform: Platform = getPlatform()
    if (platform === Platform.Web) {
      return getBaseUrl()
    } else {
      return getPlatformUrl('landings/landonhome', 'home URL')
    }
  }
  

  /** 
  Get the callback URL for password reset - points to the platform that the user is on right now
  **/
  const getCallbackPasswordResetUrl = () =>
    getPlatformUrl('password-reset-confirm', 'password reset URL')

  /** 
  Get the callback URL for signup - points to the platform that the user is on right now
  **/
  const getCallbackSignupUrl = () =>
    getPlatformUrl('auth/callback', 'signup callback URL')

  /*
  Get the landing invite URL - points to web always for universal invite link landing on web - 
  */
  const getLandingInviteUrl = (queryParams: Record<string, string>) =>
    getPlatformUrl('landings/landing-invite', 'invite URL', queryParams, true)

  logger.debug("Auth Redirects: Composable initialized", {
    hasSiteUrl: !!SITE_URL,
    nodeEnv: process.env.NODE_ENV,
    platform: getPlatform()
  })

  /** 
  Get the callback invite URL - points to the platform that the user is on right now
  **/
  const getCallbackInviteUrl = (forceWeb: boolean = false, queryParams: Record<string, string>) =>
    getPlatformUrl('auth/callback-invite', 'invite callback URL', queryParams, forceWeb)

  return {
    getBaseUrl,
    getCallbackPasswordResetUrl,
    getCallbackSignupUrl,
    getLandingInviteUrl,
    getCallbackInviteUrl,
    getPlatformUrl,  // Export for other potential uses
    getStoreUrl,
    getHomeUrl
  }
} 